import React from 'react'
import { Field, Formik } from 'formik'
import { Element, Form } from 'react-bulma-components'
import { addDays } from 'date-fns'
import { Mission } from '../../../api/missions'
import {
  dateToDateInputValue,
  dateInputToDateValue,
  timeInputValueToDate,
} from '../../../utils/date'
import { RequestMessage } from '../../request-components/request-components'
import InputField from '../../form/fields/input'
import { IsoDate } from '../../../hooks/use-calendar'
import { validateWorkPeriodTheoreticalRange } from '../validate-theoretical-range'
import type { UseMutationResult } from '../../../queries'
import OrphanedClockingsInfo from '../../clockings/orphaned-clockings-info'

export interface AddWorkPeriodFormPayload {
  start: { date: Date }
  end: { date: Date }
}

export interface AddWorkPeriodFormProps {
  /** Mission used for date bounds (min/max) and help text. */
  mission: Pick<Mission, 'start' | 'end'>
  /** Called with start/end dates when form is valid and submitted. */
  onSubmit: (payload: AddWorkPeriodFormPayload) => void
  /** Optional mutation for loading/error display (RequestMessage). */
  mutation?: UseMutationResult<any, any, any>
  /** Resets form when key changes (e.g. modal open). */
  formKey?: string
  /** Fixed day (IsoDate) — when provided, the day field is hidden and this value is used. */
  day?: IsoDate
  /** User ID for orphaned clockings info display. */
  userId?: string
  /** Tolerance range (in minutes) for matching orphaned clockings to a work period. */
  workPeriodMatchRange?: { start: number; end: number }
  /** Render prop: form content + submit handler for parent (e.g. to wire modal confirm button). */
  children: (params: { handleSubmit: () => void; content: React.ReactNode }) => React.ReactNode
}

/**
 * Builds start and end Date from a single day and start/end time strings (HH:mm).
 * Handles overnight shifts: if end time is before or equal to start time, end is the next day.
 */
export function buildStartEndDates(
  dayIso: IsoDate,
  startTime: string,
  endTime: string,
): { startDate: Date; endDate: Date } | null {
  if (!startTime || !endTime) return null
  const dayDate = dateInputToDateValue(dayIso)
  const startDate = timeInputValueToDate(dayDate, startTime)
  if (!startDate) return null
  const endDateSameDay = timeInputValueToDate(dayDate, endTime)
  if (!endDateSameDay) return null
  const endDate =
    endDateSameDay > startDate ? endDateSameDay : timeInputValueToDate(addDays(dayDate, 1), endTime)
  if (!endDate) return null
  return { startDate, endDate }
}

/**
 * Form to add a single work period: one day + start time + end time.
 * Same pattern as mission-form WorkPeriodsEditor. No modal wrapper.
 * Uses children render prop to expose handleSubmit and content (for modal or inline use).
 */
const AddWorkPeriodForm: React.FC<AddWorkPeriodFormProps> = ({
  mission,
  onSubmit,
  mutation,
  formKey,
  day: fixedDay,
  userId,
  workPeriodMatchRange,
  children,
}) => {
  const missionStart = new Date(mission.start)
  const missionEnd = mission.end ? new Date(mission.end) : undefined
  const minDate = dateToDateInputValue(missionStart) as IsoDate
  const maxDate = missionEnd ? (dateToDateInputValue(missionEnd) as IsoDate) : undefined

  return (
    <Formik
      key={formKey}
      initialValues={{
        day: fixedDay || minDate,
        startTime: '08:00',
        endTime: '17:00',
      }}
      validate={values => {
        const errors: Record<string, string> = {}
        if (!fixedDay && !values.day) errors.day = 'Champ requis'
        if (!values.startTime) errors.startTime = "L'heure de début est requise"
        if (!values.endTime) errors.endTime = "L'heure de fin est requise"
        const built = buildStartEndDates(values.day, values.startTime, values.endTime)
        if (built) {
          const rangeErrors = validateWorkPeriodTheoreticalRange(built.startDate, built.endDate)
          if (rangeErrors.startDate) errors.startTime = rangeErrors.startDate
          if (rangeErrors.endDate) errors.endTime = rangeErrors.endDate
        }
        return errors
      }}
      onSubmit={values => {
        const built = buildStartEndDates(values.day, values.startTime, values.endTime)
        if (!built) return
        onSubmit({
          start: { date: built.startDate },
          end: { date: built.endDate },
        })
      }}
    >
      {({ handleSubmit, values }) =>
        children({
          handleSubmit,
          content: (
            <>
              <Element mb={3}>
                {fixedDay ? (
                  'Choisissez les horaires de la journée.'
                ) : (
                  <>
                    Choisissez un jour dans la plage de la mission (du{' '}
                    {missionStart.toLocaleDateString('fr-FR')}
                    {missionEnd ? ` au ${missionEnd.toLocaleDateString('fr-FR')}` : ''}) et les
                    horaires de la journée.
                  </>
                )}
              </Element>
              <Form.Field>
                <Form.Label>Jour</Form.Label>
                <Form.Control>
                  <Field
                    name="day"
                    type="date"
                    component={InputField}
                    min={minDate}
                    max={maxDate}
                    required
                    disabled={!!fixedDay}
                    data-test="add-work-period-day"
                  />
                </Form.Control>
              </Form.Field>
              <Form.Field>
                <Form.Label>Heure de début</Form.Label>
                <Form.Control>
                  <Field
                    name="startTime"
                    type="time"
                    component={InputField}
                    required
                    data-test="add-work-period-start-time"
                  />
                </Form.Control>
              </Form.Field>
              <Form.Field>
                <Form.Label>Heure de fin</Form.Label>
                <Form.Control>
                  <Field
                    name="endTime"
                    type="time"
                    component={InputField}
                    required
                    data-test="add-work-period-end-time"
                  />
                </Form.Control>
              </Form.Field>
              {userId && (
                <OrphanedClockingsInfo
                  userId={userId}
                  date={dateInputToDateValue(values.day)}
                  workPeriodMatchRange={workPeriodMatchRange}
                  startTime={values.startTime}
                  endTime={values.endTime}
                />
              )}
              {mutation && <RequestMessage mutation={mutation} />}
            </>
          ),
        })
      }
    </Formik>
  )
}

export default AddWorkPeriodForm
